import type { RestEndpointQueryArgs } from '@src/providers/AppStateProvider/restEndpointBaseQuery';
import { baseRestApi, sendToastOnQueryFulfilled } from '@src/utils/rtkQuery';
import { abbreviateId, type CareExecutionReport, type CareOrder } from '@talos/kyoko';

interface ApproveCareOrderComplianceParams {
  orderID: string;
}

/**
 * Builds the compliance approval request for a held care order.
 *
 * The API defaults its timeout server-side, so this intentionally only asks the backend to wait
 * until the approval is confirmed and sends no request body.
 */
export function approveCareOrderCompliance(orderID: string): RestEndpointQueryArgs {
  return {
    method: 'POST',
    url: `/care/orders/${orderID}/compliance-approval`,
    headers: { 'Content-Type': 'application/json' },
    params: {
      'wait-for-status': 'Confirmed',
    },
    withoutBody: true,
  };
}

const careOrderComplianceApi = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    approveCareOrderCompliance: builder.mutation<CareOrder[] | CareExecutionReport[], ApproveCareOrderComplianceParams>(
      {
        query: ({ orderID }) => approveCareOrderCompliance(orderID),
        onQueryStarted: sendToastOnQueryFulfilled({
          successMessage: ({ orderID }) => `Care order #${abbreviateId(orderID)} approved.`,
          errorMessage: 'Could not approve care order compliance',
        }),
      }
    ),
  }),
});

export const { useApproveCareOrderComplianceMutation } = careOrderComplianceApi;
